import { useTranslation } from "react-i18next";
import { Ruler, Shirt, Footprints, Calendar } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProfile } from "../hooks/use-profile";

const SIZE_OPTIONS = [
  "XXS",
  "XS",
  "S",
  "M",
  "L",
  "XL",
  "XXL",
  "3XL",
  "4XL",
  "5XL",
] as const;

const fieldInputClassName =
  "w-full h-11 px-3 rounded-lg border border-border-primary dark:border-gray-600 bg-white dark:bg-gray-900 text-text-primary dark:text-gray-100 text-base placeholder:text-text-placeholder dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-text-brand dark:focus:ring-indigo-400 focus:border-transparent transition-all";

/**
 * Fit Profile Section
 * Body metrics used for wardrobe / ecommerce personalization
 */
export const FitProfileSection: React.FC = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useProfile();

  return (
    <section className="flex flex-col gap-4 md:gap-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-text-primary dark:text-gray-100 leading-6">
          {t("settings.profile.fitProfile")}
        </h3>
        <p className="text-sm text-text-tertiary dark:text-gray-400 leading-5 max-w-2xl">
          {t("settings.profile.fitProfileDescription")}
        </p>
      </div>

      <div className="rounded-2xl border border-border-secondary dark:border-gray-600 bg-white dark:bg-gray-800 p-3 sm:p-4 md:p-6">
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-center gap-2 text-text-primary dark:text-gray-200">
              <Calendar className="h-4 w-4 shrink-0 text-text-brand dark:text-indigo-400" />
              <label className="text-sm font-medium leading-5 truncate">
                {t("settings.profile.age")}
              </label>
            </div>
            <input
              type="number"
              inputMode="numeric"
              value={formData.age}
              onChange={(e) => updateFormData("age", e.target.value)}
              min={0}
              max={120}
              className={fieldInputClassName}
              placeholder={t("settings.profile.agePlaceholder")}
            />
          </div>

          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-center gap-2 text-text-primary dark:text-gray-200">
              <Ruler className="h-4 w-4 shrink-0 text-text-brand dark:text-indigo-400" />
              <label className="text-sm font-medium leading-5 truncate">
                {t("settings.profile.height")}
              </label>
            </div>
            <input
              type="number"
              inputMode="numeric"
              value={formData.height}
              onChange={(e) => updateFormData("height", e.target.value)}
              min={0}
              max={300}
              className={fieldInputClassName}
              placeholder={t("settings.profile.heightPlaceholder")}
            />
          </div>

          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-center gap-2 text-text-primary dark:text-gray-200">
              <Footprints className="h-4 w-4 shrink-0 text-text-brand dark:text-indigo-400" />
              <label className="text-sm font-medium leading-5 truncate">
                {t("settings.profile.shoeSize")}
              </label>
            </div>
            <input
              type="number"
              inputMode="numeric"
              value={formData.shoeSize}
              onChange={(e) => updateFormData("shoeSize", e.target.value)}
              min={0}
              max={60}
              className={fieldInputClassName}
              placeholder={t("settings.profile.shoeSizePlaceholder")}
            />
          </div>

          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-center gap-2 text-text-primary dark:text-gray-200">
              <Shirt className="h-4 w-4 shrink-0 text-text-brand dark:text-indigo-400" />
              <label className="text-sm font-medium leading-5 truncate">
                {t("settings.profile.size")}
              </label>
            </div>
            <Select
              value={formData.size || undefined}
              onValueChange={(value) => updateFormData("size", value)}
            >
              <SelectTrigger className="h-11 rounded-lg border-border-primary dark:border-gray-600 bg-white dark:bg-gray-900 text-text-primary dark:text-gray-100 focus:ring-2 focus:ring-text-brand dark:focus:ring-indigo-400">
                <SelectValue placeholder={t("settings.profile.selectSize")} />
              </SelectTrigger>
              <SelectContent>
                {SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={size}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </section>
  );
};
